/* vim:set sw=2: */
/* eslint-disable no-else-return, no-console */

import * as THREE from 'three'
import axios from 'axios'
import { is_lpng_initialized, initialize_lpng, decode } from 'utils/lodepng.wrapper'

import { DOWNLOAD_RETRIES_COUNT, DOWNLOAD_RETRY_DELAY_MS } from 'Renderer/constants'

const useWasm = (() => {
  const userAgent = ((window || {}).navigator || {}).userAgent
  if (!userAgent) {
    return false
  }
  // iOS detection from: http://stackoverflow.com/a/9039885/177710
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return true
  }
  // Safari detection from: https://stackoverflow.com/q/7944460
  if (/^((?!chrome|android).)*safari/i.test(userAgent)) {
    return true
  }
  return false
})()

const loadManifestTextureSize = async (manifest, name) => {
  // Get the size of a file by making an HEAD HTTP request to obtain metainformation about the url
  const requestMeta = await axios({ method: 'HEAD', url: manifest[name] })
  return !requestMeta.errors && requestMeta.status === 200
    ? +(requestMeta.headers['content-length'] || 0)
    : 0
}

const manifestImageURLS = (manifest, name) => {
  const urls = []

  const ordinaryName = name
  const webpName = `${name}_webp`

  if(manifest[webpName]) {
    urls.push(manifest[webpName])
  }

  if(manifest[ordinaryName]) {
    urls.push(manifest[ordinaryName])
  }

  return urls
}

export const manifestHasImageURL = (manifest, name) => manifestImageURLS(manifest, name).length > 0

export const loadTexture = async (url, options = {}) => {
  if (!url) {
    return null
  }

  const { retriesCount = DOWNLOAD_RETRIES_COUNT, retryDelay = DOWNLOAD_RETRY_DELAY_MS, onFail } = options

  let texture = null
  let tryNum = 0
  /* eslint-disable no-await-in-loop */
  do {
    console.log(`Downloading texture: ${url}, attempt #${tryNum + 1}`)
    texture = await new Promise(resolve => { new THREE.TextureLoader().load(url,
      resolve, null, () => resolve(null))
    })
    if (texture) {
      break
    }
    tryNum += 1
    await new Promise(resolve => { setTimeout(resolve, retryDelay) })
  } while (tryNum < retriesCount)
  /* eslint-disable no-await-in-loop */
  if (!texture && onFail) {
    onFail({ description: `Texture load failed.`, url })
  }
  return texture
}

export const loadManifestTexture = async (manifest, name, options) => {
  const texture = await loadTexture(manifest[name], options)
  if (!texture) {
    return null
  }

  const textureDownloadSize = await loadManifestTextureSize(manifest, name)

  return {
    texture,
    size: textureDownloadSize,
  }
}

export const loadManifestImagePixels = async (manifest, name, textureHelper, options) => {
  if ((useWasm || (options || {}).useWasm) && (is_lpng_initialized() || (await initialize_lpng()))) {
    let binaryPNG = null
    try {
      binaryPNG = await axios({ method: 'GET', url: manifest[name], responseType: 'arraybuffer' })
    } catch (err) {
      return null
    }

    const decodedPNG = decode(new Uint8Array(binaryPNG.data))
    if (!decodedPNG) {
      return null
    }

    const pngDownloadSize = await loadManifestTextureSize(manifest, name)

    return {
      pixels: { data: decodedPNG.output, width: decodedPNG.width, height: decodedPNG.height },
      size: pngDownloadSize,
    }
  } else {
    if (!textureHelper) {
      return null
    }
  
    const textureContainer = await loadManifestTexture(manifest, name)
    if (!textureContainer) {
      return null
    }
  
    const { width, height } = textureContainer.texture.image
    textureContainer.texture.flipY = false
    const pixelsContainer = textureHelper.readPixels(textureContainer.texture)
  
    textureContainer.texture.dispose()
    if (textureContainer.texture.image.close) {
      textureContainer.texture.image.close()
    }
  
    return {
      pixels: pixelsContainer ? { data: pixelsContainer.data, width, height } : null,
      size: textureContainer.size,
    }
  }
}